"use client";

import { useEffect, useRef, useState } from "react";

import type { Map as MapboxMapInstance } from "mapbox-gl";

import { HomeIcon } from "@/app/_components/icons/property-details/home";

interface MapboxMapProps {
  latitude: number;
  longitude: number;
  address?: string;
  zoom?: number;
  className?: string;
}

export function MapboxMap({
  latitude,
  longitude,
  address,
  zoom = 15,
  className = "",
}: MapboxMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapboxMapInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const mapboxToken = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;

    if (!mapboxToken) {
      setError("Mapbox token not configured");
      setIsLoading(false);
      return;
    }

    // multiple initializations prevent krne ke liye
    if (map.current) return;

    // Loadingg Mapbox GL dynamically (lazyyyy loading)
    const loadMapbox = async () => {
      try {
        const mapboxgl = await import("mapbox-gl");
        mapboxgl.default.accessToken = mapboxToken;
        map.current = new mapboxgl.default.Map({
          container: mapContainer.current!,
          style: "mapbox://styles/mapbox/streets-v12",
          center: [longitude, latitude],
          zoom: zoom,
          attributionControl: false,
        });

        // Adding navigation controls
        map.current.addControl(new mapboxgl.default.NavigationControl(), "top-right");

        // Creating custom red pin marker element
        const markerElement = document.createElement("div");
        markerElement.className = "custom-marker";
        markerElement.style.width = "40px";
        markerElement.style.height = "40px";
        markerElement.style.cursor = "pointer";
        markerElement.style.backgroundSize = "100%";
        markerElement.innerHTML = `
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 0C12.268 0 6 6.268 6 14C6 22 20 40 20 40C20 40 34 22 34 14C34 6.268 27.732 0 20 0ZM20 19C17.239 19 15 16.761 15 14C15 11.239 17.239 9 20 9C22.761 9 25 11.239 25 14C25 16.761 22.761 19 20 19Z" fill="#DC2626"/>
          </svg>
        `;

        // Create popup if address provided
        let popup;
        if (address) {
          popup = new mapboxgl.default.Popup({
            offset: 25,
            closeButton: false,
            closeOnClick: false,
            className: "mapbox-popup-custom",
          }).setHTML(
            `<div style="display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: #FFFFFF; border-radius: 8px; font-family: system-ui, -apple-system, sans-serif; min-width: 240px; max-width: 280px;">
              <div style="width: 32px; height: 32px; background: #F8DFB0; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="#71441B"/>
                </svg>
              </div>
              <div style="font-size: 12px; color: #374151; font-weight: 400; line-height: 1.3;">${address}</div>
            </div>`,
          );
        }

        // Add marker with custom element - pass element as first argument
        const marker = new mapboxgl.default.Marker(markerElement, {
          anchor: "bottom",
        })
          .setLngLat([longitude, latitude])
          .addTo(map.current);

        // Add popup if provided and open it immediately
        if (popup) {
          marker.setPopup(popup);
          marker.togglePopup();
        }

        // Wait for map to load
        map.current.on("load", () => {
          setIsLoading(false);
        });

        map.current.on("error", (e: unknown) => {
          console.error("Mapbox error:", e);
          setError("Failed to load map");
          setIsLoading(false);
        });
      } catch (err) {
        console.error("Error loading Mapbox:", err);
        setError("Failed to initialize map");
        setIsLoading(false);
      }
    };

    loadMapbox();

    // Cleanup
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [address, latitude, longitude, zoom]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="text-center">
          <HomeIcon />
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          {address && <p className="mt-1 text-xs text-gray-500">{address}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={mapContainer} className="h-full w-full" />

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80">
          <div className="flex flex-col items-center gap-2">
            <div className="border-t-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-gray-300" />
            <p className="text-sm text-gray-600">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
}
